import React, { useEffect, useState } from 'react';
import { Alert, Button, Col, Form, Modal, Row } from '@/components/ui';
import {
  RESOURCE_TYPE_OPTIONS,
  RESOURCE_VEHICLE_TYPE_OPTIONS,
  ResourceType,
  Vendor,
  VendorResource,
  canonicalizeResourceUnit,
  resourceCarriesFuelSurcharge,
  resourceUnitSelectOptions,
  vendorService,
  unitsOfMeasureMatch,
} from '../../../services/vendorService';

interface Props {
  show: boolean;
  onHide: () => void;
  vendor: Vendor;
  resource?: VendorResource | null;
  onSaved: () => Promise<void> | void;
}

interface FormState {
  name: string;
  resourceType: ResourceType;
  unit: string;
  isFixedPrice: boolean;
  makeAndModel: string;
  overtimeEligible: boolean;
  comments: string;
  isActive: boolean;
  vehicleType: string;
  requiresVehicleInsurance: boolean;
  vehicleInsuranceExpDate: string;
  customerUnitNumber: string;
  serviceAreaIds: number[];
  bundleComponents: Array<{ componentResourceId: number; qty: number }>;
}

const EMPTY: FormState = {
  name: '',
  resourceType: 'EQUIPMENT',
  unit: '',
  isFixedPrice: false,
  makeAndModel: '',
  overtimeEligible: false,
  comments: '',
  isActive: true,
  vehicleType: '',
  requiresVehicleInsurance: false,
  vehicleInsuranceExpDate: '',
  customerUnitNumber: '',
  serviceAreaIds: [],
  bundleComponents: [],
};

/**
 * Type-aware resource form (HE-M03, HE-M04, HE-M16).
 *
 * Two things the form makes explicit rather than leaving implicit:
 *  - selecting a type tells you which markup bucket the cost will land in, so nobody has to
 *    remember the rule (and nobody is asked to type a percentage);
 *  - Bundle reveals a component picker, because a bundle with no components cannot be priced,
 *    and hides unit-level fields (make/model, vendor unit number, areas, insurance, OT)
 *    because those already live on the components. The bundle itself defines the unit of
 *    measurement, and every component must use that same unit.
 */
const ResourceModal: React.FC<Props> = ({ show, onHide, vendor, resource, onSaved }) => {
  const [form, setForm] = useState<FormState>(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [insuranceFile, setInsuranceFile] = useState<File | null>(null);
  const [removeInsuranceFile, setRemoveInsuranceFile] = useState(false);

  useEffect(() => {
    if (!show) return;
    setError(null);
    setInsuranceFile(null);
    setRemoveInsuranceFile(false);
    setForm(
      resource
        ? {
            name: resource.name,
            resourceType: resource.resourceType,
            unit: canonicalizeResourceUnit(resource.unit),
            isFixedPrice: resource.isFixedPrice,
            makeAndModel: resource.makeAndModel ?? '',
            overtimeEligible: resource.overtimeEligible,
            comments: resource.comments ?? '',
            isActive: resource.isActive,
            vehicleType: resource.vehicleType ?? '',
            requiresVehicleInsurance: resource.requiresVehicleInsurance,
            // <input type="date"> wants YYYY-MM-DD, and the value is stored at UTC midnight —
            // slicing the ISO string keeps the stored day rather than the viewer's local one.
            vehicleInsuranceExpDate: resource.vehicleInsuranceExpDate
              ? resource.vehicleInsuranceExpDate.slice(0, 10)
              : '',
            customerUnitNumber: resource.customerUnitNumber ?? '',
            serviceAreaIds: resource.serviceAreas?.map((sa) => sa.serviceAreaId) ?? [],
            bundleComponents:
              resource.bundleComponents?.map((c) => ({
                componentResourceId: c.componentResourceId,
                qty: c.qty,
              })) ?? [],
          }
        : { ...EMPTY, serviceAreaIds: [] }
    );
  }, [show, resource]);

  /**
   * Mirrors resourceTypeToEffortType() on the server. Shown, never sent — the server derives
   * it independently, so this is a preview of the rule, not an input to it.
   */
  const markupBucket = (): string => {
    if (form.isFixedPrice) return 'No markup — charged as entered';
    switch (form.resourceType) {
      case 'EQUIPMENT':
      case 'ATTACHMENT':
        return 'Hired equipment markup';
      case 'SUBCONTRACTOR':
        return 'Subcontractor markup';
      case 'MATERIAL':
        return 'Material markup';
      case 'BUNDLE':
        return 'Each component uses its own markup';
      default:
        return '—';
    }
  };

  const toggleArea = (id: number) =>
    setForm((prev) => ({
      ...prev,
      serviceAreaIds: prev.serviceAreaIds.includes(id)
        ? prev.serviceAreaIds.filter((x) => x !== id)
        : [...prev.serviceAreaIds, id],
    }));

  const candidateComponents = (vendor.resources ?? []).filter(
    (r) => r.resourceType !== 'BUNDLE' && r.id !== resource?.id
  );

  const toggleComponent = (componentResourceId: number) =>
    setForm((prev) => ({
      ...prev,
      bundleComponents: prev.bundleComponents.some((c) => c.componentResourceId === componentResourceId)
        ? prev.bundleComponents.filter((c) => c.componentResourceId !== componentResourceId)
        : [...prev.bundleComponents, { componentResourceId, qty: 1 }],
    }));

  const setComponentQty = (componentResourceId: number, qty: number) =>
    setForm((prev) => ({
      ...prev,
      bundleComponents: prev.bundleComponents.map((c) =>
        c.componentResourceId === componentResourceId ? { ...c, qty } : c
      ),
    }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const isBundle = form.resourceType === 'BUNDLE';
    if (!form.name.trim()) {
      setError('A resource name is required.');
      return;
    }
    if (!isBundle && form.serviceAreaIds.length === 0) {
      setError('Select at least one service area — a resource with none cannot be selected or priced.');
      return;
    }
    if (isBundle && !form.unit.trim()) {
      setError('A bundle needs a unit of measurement.');
      return;
    }
    if (isBundle && form.bundleComponents.length === 0) {
      setError('A bundle needs at least one component resource.');
      return;
    }
    if (
      isBundle
      && form.bundleComponents.some((c) => {
        const component = (vendor.resources ?? []).find((r) => r.id === c.componentResourceId);
        return !unitsOfMeasureMatch(form.unit, component?.unit);
      })
    ) {
      setError('Every bundle component must use the same unit of measurement as the bundle.');
      return;
    }
    // The toggle exists to make a lapse visible; on without a date records nothing and the
    // resource would sit in the table claiming cover it cannot evidence.
    if (!isBundle && form.requiresVehicleInsurance && !form.vehicleInsuranceExpDate) {
      setError('Enter the vehicle insurance expiry date, or turn off "Requires vehicle insurance".');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      // A bundle defines unit of measurement itself. Make/model, OT, insurance, and vendor
      // unit number stay on the components. Service areas are inherited from the vendor on create.
      const vendorAreaIds = (vendor.serviceAreas ?? []).map((sa) => sa.serviceAreaId);
      const payload = isBundle
        ? {
            name: form.name.trim(),
            resourceType: form.resourceType,
            unit: form.unit.trim(),
            isFixedPrice: false,
            overtimeEligible: false,
            comments: form.comments || undefined,
            vehicleType: null as string | null,
            requiresVehicleInsurance: false,
            vehicleInsuranceExpDate: null as string | null,
            makeAndModel: null as string | null,
            customerUnitNumber: null as string | null,
            serviceAreaIds: resource ? undefined : vendorAreaIds,
            bundleComponents: form.bundleComponents,
          }
        : {
            name: form.name.trim(),
            resourceType: form.resourceType,
            unit: form.unit || undefined,
            isFixedPrice: form.isFixedPrice,
            makeAndModel: form.makeAndModel || undefined,
            overtimeEligible: form.overtimeEligible,
            comments: form.comments || undefined,
            vehicleType: resourceCarriesFuelSurcharge(form.resourceType)
              ? form.vehicleType.trim() || null
              : 'N/A',
            requiresVehicleInsurance: form.requiresVehicleInsurance,
            vehicleInsuranceExpDate: form.requiresVehicleInsurance
              ? form.vehicleInsuranceExpDate
              : null,
            customerUnitNumber: form.customerUnitNumber.trim() || null,
            serviceAreaIds: form.serviceAreaIds,
            bundleComponents: undefined,
          };

      if (resource) {
        await vendorService.updateResource(resource.id, { ...payload, isActive: form.isActive });
        if (!isBundle && form.requiresVehicleInsurance) {
          if (insuranceFile) {
            await vendorService.uploadVehicleInsurance(resource.id, insuranceFile);
          } else if (removeInsuranceFile && resource.vehicleInsuranceFileUrl) {
            await vendorService.clearVehicleInsurance(resource.id);
          }
        }
      } else {
        const created = await vendorService.createResource(vendor.id, {
          ...payload,
          serviceAreaIds: payload.serviceAreaIds ?? vendorAreaIds,
        });
        if (!isBundle && form.requiresVehicleInsurance && insuranceFile) {
          await vendorService.uploadVehicleInsurance(created.id, insuranceFile);
        }
      }
      await onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save resource');
    } finally {
      setSaving(false);
    }
  };

  const vendorAreas = vendor.serviceAreas ?? [];
  const isBundle = form.resourceType === 'BUNDLE';

  return (
    <Modal show={show} onHide={onHide} size="2xl">
      <Modal.Header closeButton>
        <Modal.Title>{resource ? `Edit ${resource.name}` : 'Add Resource'}</Modal.Title>
      </Modal.Header>
      {/* The form sits between the panel and Modal.Body, so it must carry the flex-column
          contract itself. Without it Body grows to full content height and the footer is
          clipped by the panel's overflow. */}
      <Form onSubmit={handleSubmit} className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col">
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}

          <Row>
            <Col md={6}>
              <Form.Group className="tw-mb-3">
                <Form.Label>Name *</Form.Label>
                <Form.Control
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="tw-mb-3">
                <Form.Label>Type *</Form.Label>
                <Form.Select
                  value={form.resourceType}
                  onChange={(e) => {
                    const resourceType = e.target.value as ResourceType;
                    setForm((prev) => ({
                      ...prev,
                      resourceType,
                      ...(resourceType === 'BUNDLE'
                        ? {
                            isFixedPrice: false,
                            overtimeEligible: false,
                            requiresVehicleInsurance: false,
                            makeAndModel: '',
                            customerUnitNumber: '',
                            vehicleInsuranceExpDate: '',
                          }
                        : {}),
                    }));
                  }}
                  disabled={!!resource}
                >
                  {RESOURCE_TYPE_OPTIONS.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </Form.Select>
                {resource && (
                  <Form.Text>
                    Type cannot be changed after creation — it decides how existing costs were
                    categorised.
                  </Form.Text>
                )}
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="tw-mb-3" controlId="resource-unit">
                <Form.Label>{isBundle ? 'Unit *' : 'Unit'}</Form.Label>
                <Form.Select
                  value={form.unit}
                  required={isBundle}
                  aria-label="Unit"
                  onChange={(e) => {
                    const unit = e.target.value;
                    setForm((prev) => ({
                      ...prev,
                      unit,
                      bundleComponents:
                        prev.resourceType === 'BUNDLE'
                          ? prev.bundleComponents.filter((c) => {
                              const component = (vendor.resources ?? []).find(
                                (r) => r.id === c.componentResourceId,
                              );
                              return unitsOfMeasureMatch(unit, component?.unit);
                            })
                          : prev.bundleComponents,
                    }));
                  }}
                >
                  <option value="">Choose…</option>
                  {resourceUnitSelectOptions(form.unit).map((unit) => (
                    <option key={unit} value={unit}>
                      {unit}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          {resourceCarriesFuelSurcharge(form.resourceType) && (
            <Row>
              <Col md={6}>
                <Form.Group className="tw-mb-3" controlId="resource-vehicle-type">
                  <Form.Label>Road class</Form.Label>
                  <Form.Select
                    value={form.vehicleType}
                    onChange={(e) => setForm({ ...form, vehicleType: e.target.value })}
                  >
                    <option value="">Choose…</option>
                    {RESOURCE_VEHICLE_TYPE_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Text className="tw-text-content-muted">
                    On-Road and Off-Road select the matching fuel surcharge. N/A means no surcharge
                    for this unit.
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
          )}

          <Alert variant="info" className="tw-mb-3">
            <strong>Costing:</strong> {markupBucket()}. Markup is applied per task from the existing
            costing settings — it is never entered here.
          </Alert>

          {!isBundle && (
          <Row>
            <Col md={6}>
              <Form.Group className="tw-mb-3">
                <Form.Label>Make and Model</Form.Label>
                <Form.Control
                  type="text"
                  value={form.makeAndModel}
                  onChange={(e) => setForm({ ...form, makeAndModel: e.target.value })}
                />
              </Form.Group>
              <Form.Group className="tw-mb-3">
                <Form.Label>Vendor Unit Number</Form.Label>
                <Form.Control
                  type="text"
                  value={form.customerUnitNumber}
                  onChange={(e) => setForm({ ...form, customerUnitNumber: e.target.value })}
                  placeholder="e.g. AC-114"
                />
                <Form.Text className="tw-text-content-muted">
                  The vendor&rsquo;s own number for this unit. Their invoices reference it instead
                  of a description, so without it an invoice line cannot be matched back here.
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="tw-mb-3 tw-mt-4">
                <Form.Check
                  type="checkbox"
                  id="resource-fixed-price"
                  label="Fixed price — no markup applies"
                  checked={form.isFixedPrice}
                  onChange={(e) => setForm({ ...form, isFixedPrice: e.target.checked })}
                />
                <Form.Check
                  type="checkbox"
                  id="resource-ot"
                  label="Overtime eligible"
                  checked={form.overtimeEligible}
                  onChange={(e) => setForm({ ...form, overtimeEligible: e.target.checked })}
                />
                <Form.Check
                  type="checkbox"
                  id="resource-requires-vehicle-insurance"
                  label="Requires vehicle insurance"
                  checked={form.requiresVehicleInsurance}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setForm({
                      ...form,
                      requiresVehicleInsurance: checked,
                      // Drop the date when the toggle goes off, so it cannot linger and later
                      // reappear against a resource nobody intended to track.
                      vehicleInsuranceExpDate: checked ? form.vehicleInsuranceExpDate : '',
                    });
                    if (!checked) {
                      setInsuranceFile(null);
                      setRemoveInsuranceFile(false);
                    }
                  }}
                />
                {form.requiresVehicleInsurance && (
                  <Form.Group className="tw-mb-3 tw-ml-4 tw-mt-2">
                    <Form.Label>Vehicle Insurance Expiry *</Form.Label>
                    <Form.Control
                      type="date"
                      value={form.vehicleInsuranceExpDate}
                      onChange={(e) =>
                        setForm({ ...form, vehicleInsuranceExpDate: e.target.value })
                      }
                      required
                    />
                    <Form.Text className="tw-text-content-muted">
                      Flagged on the resource list when missing or expired. Unlike the vendor&rsquo;s
                      liability cover, this never blocks the vendor from being activated.
                    </Form.Text>
                    <Form.Label className="tw-mt-3">Insurance certificate</Form.Label>
                    {resource?.vehicleInsuranceFileUrl && !removeInsuranceFile && !insuranceFile && (
                      <div className="tw-mb-2 tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                        <a
                          href={resource.vehicleInsuranceFileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <i className="bi bi-paperclip tw-mr-1" />
                          {resource.vehicleInsuranceFileName || 'View certificate'}
                        </a>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          type="button"
                          onClick={() => {
                            setRemoveInsuranceFile(true);
                            setInsuranceFile(null);
                          }}
                        >
                          Remove
                        </Button>
                      </div>
                    )}
                    <Form.Control
                      type="file"
                      accept=".pdf,.png,.jpg,.jpeg,.webp"
                      onChange={(e) => {
                        const input = e.target as HTMLInputElement;
                        setInsuranceFile(input.files?.[0] ?? null);
                        if (input.files?.[0]) setRemoveInsuranceFile(false);
                      }}
                    />
                    <Form.Text className="tw-text-content-muted">
                      {resource?.vehicleInsuranceFileUrl && !removeInsuranceFile
                        ? 'Choose a file to replace the current certificate. Optional — expiry is what the list flags.'
                        : 'Optional. PDF or image of the unit\u2019s policy. Expiry is what the list flags; this is the evidence behind it.'}
                    </Form.Text>
                  </Form.Group>
                )}
              </Form.Group>
            </Col>
          </Row>
          )}

          {!isBundle && (
          <Form.Group className="tw-mb-3">
            <Form.Label>Offered in Service Areas *</Form.Label>
            <div className="tw-text-sm tw-text-content-muted tw-mb-2">
              Only areas {vendor.name} is approved to operate in are listed.
            </div>
            <div className="tw-flex tw-flex-wrap tw-gap-3">
              {vendorAreas.map((sa) => (
                <Form.Check
                  key={sa.id}
                  type="checkbox"
                  id={`resource-sa-${sa.serviceAreaId}`}
                  label={sa.serviceArea.name}
                  checked={form.serviceAreaIds.includes(sa.serviceAreaId)}
                  onChange={() => toggleArea(sa.serviceAreaId)}
                />
              ))}
            </div>
          </Form.Group>
          )}

          {isBundle && (
            <Form.Group className="tw-mb-3">
              <Form.Label>Bundle Components *</Form.Label>
              <div className="tw-text-sm tw-text-content-muted tw-mb-2">
                A bundle is one selectable line made of several resources — a truck plus operator
                plus attachment. It prices as the sum of its components at each component&apos;s own
                markup, unless the bundle itself is given a rate. Every component must use this
                bundle&apos;s unit of measurement.
              </div>
              {!form.unit ? (
                <Alert variant="info" className="tw-mb-2">
                  Choose a unit of measurement first. Only resources billed in that unit can be added.
                </Alert>
              ) : null}
              {candidateComponents.length === 0 ? (
                <Alert variant="warning" className="tw-mb-0">
                  This vendor has no non-bundle resources to combine yet.
                </Alert>
              ) : (
                candidateComponents.map((c) => {
                  const selected = form.bundleComponents.find((bc) => bc.componentResourceId === c.id);
                  const compatible = Boolean(form.unit) && unitsOfMeasureMatch(form.unit, c.unit);
                  return (
                    <div key={c.id} className="tw-flex tw-items-center tw-gap-3 tw-mb-2">
                      <Form.Check
                        type="checkbox"
                        id={`bundle-c-${c.id}`}
                        label={`${c.name} (${c.resourceType.toLowerCase()}${c.unit ? `, ${c.unit}` : ''})`}
                        checked={!!selected}
                        disabled={!compatible}
                        onChange={() => toggleComponent(c.id)}
                      />
                      {selected && (
                        <Form.Control
                          type="number"
                          min={0}
                          step="0.01"
                          style={{ width: '110px' }}
                          value={selected.qty}
                          onChange={(e) => setComponentQty(c.id, parseFloat(e.target.value) || 0)}
                          aria-label={`Quantity of ${c.name}`}
                        />
                      )}
                    </div>
                  );
                })
              )}
            </Form.Group>
          )}

          <Form.Group className="tw-mb-3">
            <Form.Label>Comments</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={form.comments}
              onChange={(e) => setForm({ ...form, comments: e.target.value })}
            />
          </Form.Group>

          {resource && (
            <Form.Group className="tw-mb-3">
              <Form.Check
                type="checkbox"
                id="resource-active"
                label="Active"
                checked={form.isActive}
                onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
              />
            </Form.Group>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="outline-secondary" type="button" onClick={onHide} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={saving}>
            {saving ? 'Saving…' : resource ? 'Save Changes' : 'Add Resource'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default ResourceModal;
